<script setup>
import {computed, onMounted, watch, ref} from 'vue'
import {useStore} from 'vuex'
import {useRouter} from 'vue-router'
import {Settings, Users, CreditCard, ChevronRight, ChevronDown, Plus} from 'lucide-vue-next'
import { useFeatures } from '@/composables/useFeatures'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { FormCreateOrg } from '@/components/forms'

const router = useRouter()
const store = useStore()
const { showBillingPage } = useFeatures()
const isOpen = ref(false)
const showOrgList = ref(false)
const showCreateOrgForm = ref(false)

const map = computed(() => store.getters['geo/getMap'])
const orgs = computed(() => store.getters['org/getOrgs'])
const selectedOrgId = computed(() => store.getters['org/getSelected'])
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])
const isLoggedIn = computed(() => store.getters['getIsLoggedIn'])

const currentOrg = computed(() => {
  return selectedOrg.value || null
})

const sortedOrgs = computed(() => {
  return [...orgs.value].sort((a, b) =>
    a.organization_name.localeCompare(b.organization_name)
  )
})

const handleOrgChange = (orgId) => {
  store.dispatch('org/setSelectedOrg', orgId)
  isOpen.value = false
  showOrgList.value = false
  router.push({name: 'Dashboard'})
}

const initSelectedOrg = () => {
  store.dispatch('org/initSelectedOrg')
}

const resetView = () => {
  const defaultOptions = {
    center: [-21.874, 42.76],
    zoom: 0
  }
  map.value.setCenter(defaultOptions.center)
  map.value.setZoom(defaultOptions.zoom)

  router.push('/')
}

const goToSettings = () => {
  if (selectedOrgId.value) {
    router.push({ name: 'OrgSettings', params: { org_id: selectedOrgId.value }})
  }
  isOpen.value = false
}

const goToMembers = () => {
  if (selectedOrgId.value) {
    router.push({ name: 'OrgMembers', params: { org_id: selectedOrgId.value }})
  }
  isOpen.value = false
}

const goToBilling = () => {
  if (selectedOrgId.value) {
    router.push({ name: 'OrgBilling', params: { org_id: selectedOrgId.value }})
  }
  isOpen.value = false
}

const toggleOrgList = () => {
  showOrgList.value = !showOrgList.value
}

const handleCreateOrganization = () => {
  showCreateOrgForm.value = true
  isOpen.value = false
  showOrgList.value = false
}

const handleCloseForm = () => {
  showCreateOrgForm.value = false
}

const handleFormSubmit = async (result) => {
  if (result.success) {
    showCreateOrgForm.value = false

    try {
      await store.dispatch('org/fetchOrgs')

      if (orgs.value.length > 0) {
        // Set the newly created organization as selected
        const newOrg = orgs.value.find(org => org.organization_id === result.organization_id) || orgs.value[0]
        await store.dispatch('org/setSelectedOrg', newOrg.organization_id.toString())
      }
    } catch (error) {
      console.error('Failed to fetch organizations after creation:', error)
    }
  }
}

onMounted(() => {
  if (isLoggedIn.value) {
    initSelectedOrg()
  }
})

// Watch for orgs changes to initialize selected org
watch(() => orgs.value, (newOrgs) => {
  if (newOrgs.length > 0 && !selectedOrgId.value) {
    initSelectedOrg()
  }
})
</script>

<template>
<div v-if="isLoggedIn" class="mr-3">
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <Button variant="ghost" class="justify-start p-1 border-0 h-auto font-normal">
        <div class="flex items-center">
          <div class="hidden md:flex md:mr-2">
            <Avatar class="w-9 h-9 border border-gray-300 cursor-pointer hover:opacity-80 rounded-lg">
              <AvatarImage v-if="currentOrg && currentOrg.avatar_url" :src="currentOrg.avatar_url" :alt="currentOrg.organization_name" />
              <img v-else src="@/assets/img/main-logo.svg" alt="logo" style="height: 32px; min-width: 32px;" />
            </Avatar>
          </div>
          <div class="flex items-center space-x-2">
            <Avatar class="w-6 h-6 md:hidden">
              <AvatarImage v-if="currentOrg && currentOrg.avatar_url" :src="currentOrg.avatar_url" :alt="currentOrg.organization_name" />
              <img v-else src="@/assets/img/main-logo.svg" alt="logo" />
            </Avatar>
            <div class="flex flex-col items-start">
              <span class="text-sm font-medium truncate max-w-[150px] sm:max-w-[200px] md:max-w-none">
                {{ selectedOrg?.organization_name }}
              </span>
            </div>
            <ChevronDown class="w-4 h-4" />
          </div>
        </div>
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-64 p-0" align="start">
      <div class="p-0.5">
        <Button @click="goToSettings" variant="ghost" class="w-full justify-start">
          <Settings class="w-4 h-4 mr-3" />
          Settings
        </Button>
        <Button @click="goToMembers" variant="ghost" class="w-full justify-start">
          <Users class="w-4 h-4 mr-3" />
          Members
        </Button>
        <Button v-if="showBillingPage" @click="goToBilling" variant="ghost" class="w-full justify-start">
          <CreditCard class="w-4 h-4 mr-3" />
          Billing
        </Button>
        <Button
          @click="toggleOrgList"
          variant="ghost"
          class="w-full flex justify-between"
        >
          Switch Organization
          <ChevronRight class="w-4.5 h-4.5" :class="{'rotate-90': showOrgList}" />
        </Button>
        <div v-if="showOrgList">
          <div class="pt-1 w-full max-h-[40vh] overflow-y-auto">
            <Button
              v-for="org in sortedOrgs"
              :key="org.organization_id"
              @click="handleOrgChange(org.organization_id.toString())"
              variant="ghost"
              class="w-full justify-start"
            >
              <Avatar class="w-5 h-5 mr-3 rounded-md">
                <AvatarImage v-if="org.avatar_url" :src="org.avatar_url" :alt="org.organization_name" />
                <img v-else src="@/assets/img/main-logo.svg" alt="logo" />
              </Avatar>
              {{ org.organization_name }}
            </Button>

            <Button @click="handleCreateOrganization" variant="ghost" class="w-full justify-start">
              <Plus class="w-4 h-4 mr-3" />
              Create a new organization
            </Button>
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>

  <FormCreateOrg
    :open="showCreateOrgForm"
    @close="handleCloseForm"
    @submit="handleFormSubmit"
  />
</div>
</template>
